import { getAuthToken } from '../utils/authStorage';

// Best-effort UI breadcrumb telemetry for prod UX repro (drug-lab flicker).
// PRIVACY: never reads input values, never serializes the JWT into meta, and only
// sends the bearer in the Authorization header (decoded server-side to user_id,
// never stored). Click breadcrumb captures data-node-id / aria-label / data-testid /
// id, or falls back to a structural tag.class descriptor (e.g. "button.quiz-option").
// It NEVER captures element text content, so MCQ answer-choice text is never
// persisted server-side. The module must NEVER throw into app code.

const ENDPOINT = '/api/telemetry/ui-events'; // resolved against API base; see initTelemetry
let API_BASE = ''; // set by initTelemetry() from the shared apiClient baseURL
let ANON_ID = null;
let queue = [];
let flushTimer = null;
const FLUSH_MS = 4000;
const MAX_QUEUE = 40; // flush early if it fills

function anonId() {
  if (ANON_ID) return ANON_ID;
  try {
    ANON_ID = localStorage.getItem('ui_anon_id');
    if (!ANON_ID) {
      ANON_ID = 'anon-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('ui_anon_id', ANON_ID);
    }
  } catch {
    ANON_ID = 'anon-mem';
  }
  return ANON_ID;
}

export function track(event, fields = {}) {
  if (!event) return;
  try {
    queue.push({
      event,
      surface: fields.surface,
      target_id: fields.target_id,
      rxcui: fields.rxcui,
      node_role: fields.node_role,
      combat_session_id: fields.combat_session_id,
      route: fields.route ?? window.location?.pathname,
      client_ts: new Date().toISOString(),
      meta: fields.meta || {},
    });
    if (queue.length >= MAX_QUEUE) flush();
    else if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_MS);
  } catch {
    /* telemetry must never throw into app */
  }
}

export function flush({ beacon = false } = {}) {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!queue.length) return;
  const events = queue.splice(0, 50);
  const url = API_BASE + ENDPOINT;
  const body = JSON.stringify({ anon_id: anonId(), events });
  const token = (() => {
    try {
      return getAuthToken();
    } catch {
      return null;
    }
  })();
  try {
    if (beacon && navigator.sendBeacon && !token) {
      // sendBeacon cannot set Authorization; only use it for anon unload flushes
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
    fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: 'Bearer ' + token } : {}),
      },
      body,
    }).catch(() => {});
  } catch {
    /* telemetry must never throw into app */
  }
}

export function initTelemetry({ apiBase = '' } = {}) {
  if (typeof window === 'undefined') return;
  if (window.__uiTelemetryInstalled) return;
  window.__uiTelemetryInstalled = true;
  API_BASE = apiBase || '';

  // delegated capture-phase click breadcrumb
  document.addEventListener(
    'click',
    (ev) => {
      try {
        const t = ev.target?.closest?.("[data-node-id],button,a,[role='button']") || ev.target;
        if (!t) return;
        // structural descriptor ONLY - never element text content. Falling back to
        // textContent here would persist MCQ answer-choice text (the .quiz-option
        // buttons in MCQOptions carry no data-node-id / aria-label) = answer-content
        // PII platform-wide. tag.class is enough to identify what was clicked for repro.
        const clsAttr = t.getAttribute?.('class');
        const cls = (typeof clsAttr === 'string' ? clsAttr : '').trim().split(/\s+/)[0] || '';
        const label =
          t.getAttribute?.('data-node-id') ||
          t.getAttribute?.('aria-label') ||
          t.getAttribute?.('data-testid') ||
          t.id ||
          ((t.tagName || 'EL').toLowerCase() + (cls ? '.' + cls : ''));
        track('click', { target_id: label, surface: t.getAttribute?.('data-surface') || undefined });
      } catch {
        /* swallow */
      }
    },
    true,
  );

  // window errors
  window.addEventListener('error', (e) => {
    track('window_error', {
      meta: {
        msg: String(e.message || '').slice(0, 200),
        src: String(e.filename || '').slice(0, 120),
        line: e.lineno,
      },
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    track('unhandledrejection', { meta: { reason: String(e.reason || '').slice(0, 200) } });
  });

  // console.error patch (does not capture answer content - message text only, clipped)
  const origErr = console.error.bind(console);
  console.error = (...a) => {
    try {
      track('console_error', { meta: { msg: a.map(String).join(' ').slice(0, 200) } });
    } catch {
      /* swallow */
    }
    origErr(...a);
  };

  // flush on hide/unload (anon -> beacon, authed -> keepalive fetch)
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush({ beacon: true });
  });
  window.addEventListener('pagehide', () => flush({ beacon: true }));
}

export function trackRoute(pathname) {
  track('route_change', { route: pathname, surface: 'route' });
}
